import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useWindowWidth } from '../hooks';

// The bar under a run clicked on the board (the director, 21 Oct 2026: "A thin
// bar appears under the job when you click on the job ... complete, edit, move
// to, and delete"). It replaces three icons that appeared on hover -- a one-day
// card had no room for them, they had no words, and a tablet never showed
// them -- and the keyboard hint that used to float at the bottom of the screen.
//
// What the run is, in the words a narrow card has no room for, on one line
// on a desktop or a tablet held sideways -- on a narrower screen it breaks
// only between whole pieces, never inside a date (21 Oct 2026) -- then its
// buttons, small. Drawn over the board just under the card (above it where
// the screen ends), as wide as it needs whatever the card's width, and
// following the card when the board scrolls. Closes on Esc (the board's), a
// click anywhere else, or once a button is pressed. The keys are explained in
// the menu's Keyboard shortcuts, not here.
//
// anchor: CSS selector of the card   title: the run's name   detail: [pieces]
// actions: [{ key, label, icon, tone, onClick, disabled, title }]

const BORDER = 'rgba(99,102,241,0.45)';
const WIDE = 1024;   // a desktop, or a tablet held sideways

export default function RunBar({ anchor, title, detail = [], actions = [], onClose }) {
  const ref = useRef(null);
  const wide = useWindowWidth() >= WIDE;
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const card = document.querySelector(anchor);
    const bar = ref.current;
    if (!card || !bar) { setPos(p => (p === null ? p : null)); return; }
    const r = card.getBoundingClientRect();
    const w = bar.offsetWidth, h = bar.offsetHeight;
    const below = r.bottom + 6 + h <= window.innerHeight - 8;
    const top = Math.round(below ? r.bottom + 6 : Math.max(8, r.top - h - 6));
    const left = Math.round(Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - w - 8)));
    // Off the visible board (scrolled away): hidden, not closed.
    const gone = r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth;
    setPos(p => (p && p.top === top && p.left === left && p.gone === gone ? p : { top, left, gone }));
  }, [anchor]);

  useLayoutEffect(() => { place(); });
  useEffect(() => {
    const onScroll = () => place();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    // A click anywhere but the bar or its card closes it; the card's own click
    // is the board's (it toggles the selection).
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      const card = document.querySelector(anchor);
      if (card && card.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('pointerdown', onDown, true);
    };
  }, [anchor, place, onClose]);

  return createPortal(
    <div ref={ref} role="toolbar" aria-label={title}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, zIndex: 1150,
        visibility: pos && !pos.gone ? 'visible' : 'hidden',
        maxWidth: wide ? 'min(760px, calc(100vw - 16px))' : 'calc(100vw - 16px)', boxSizing: 'border-box',
        background: 'rgba(17,20,28,0.96)', backdropFilter: 'blur(8px)', border: `1px solid ${BORDER}`, borderRadius: 9,
        padding: '6px 8px', boxShadow: '0 10px 32px rgba(0,0,0,0.55)', fontFamily: 'system-ui', color: '#f0f2f5',
      }}>
      {/* One line: the name, then each piece. Wide, it stays one line and the
          name gives way first; narrow, it breaks between pieces only. Each
          piece carries its "·" in front of it, in a margin the outer box cuts
          off -- so the one that starts a line is never seen. */}
      <div style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: wide ? 'nowrap' : 'wrap', rowGap: 1, marginLeft: -12, fontSize: 11.5, lineHeight: 1.35, minWidth: 0 }}>
          {[title, ...detail].map((d, i) => (
            <span key={i} style={{ position: 'relative', paddingLeft: 12, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                                   ...(i === 0 ? { fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 } : { color: '#9ca3af', flexShrink: 0 }) }}>
              <span aria-hidden="true" style={{ position: 'absolute', left: 4, color: '#4b5563', fontWeight: 400 }}>·</span>{d}
            </span>
          ))}
        </div>
      </div>
      {actions.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5, flexWrap: 'wrap' }}>
          {actions.map(a => (
            <button key={a.key} type="button" onClick={a.disabled ? undefined : a.onClick} disabled={a.disabled} title={a.title}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, lineHeight: 1.5,
                       fontFamily: 'system-ui', cursor: a.disabled ? 'not-allowed' : 'pointer', opacity: a.disabled ? 0.4 : 1,
                       background: `${a.tone}1f`, border: `1px solid ${a.tone}66`, color: a.tone }}>
              <i className={`fa-solid ${a.icon}`} style={{ fontSize: 9 }} />{a.label}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
